<template>
  <div>
    <div class="flex justify-between items-center">
      <JoszakiHeading class="first-letter:capitalize">
        {{ question.title }}
      </JoszakiHeading>
      <span class="flex flex-row gap-5 text-lg">
        <IconComponent
          v-if="me"
          :icon="subscribeIcon"
          :class="{ 'animate-spin cursor-wait': isSubscribing }"
          class="cursor-pointer hover:text-primary"
          @click.native="setSubscribe(!isSubscribed)"
        />
        <IconComponent
          v-if="me?.isAdmin"
          icon="exclamation-circle"
          class="cursor-pointer hover:text-primary"
          @click.native="openReportModal"
        />
      </span>
    </div>
    <QuestionPills v-if="$mq !== 'mobile'" :question="question" class="mb-2" />
    <JoszakiCard>
      <div class="grid grid-cols-[auto_1fr] gap-2">
        <UserAvatar
          :image="
            $helpers.getProfilePictureThumbnailUrl(
              question.person.profilePhotoId
            )
          "
        />
        <div>
          <p class="font-bold text-lg">
            {{ $t("questionPage.questionOf", { name: question.person.name }) }}
          </p>
          <p class="text-lg whitespace-pre-line">
            {{ question.body }}
          </p>
          <p class="text-xs font-normal text-black/50 text-right">
            {{ formatDate(question.created) }}
            <template v-if="question.updated">
              {{
                $t("questionPage.editedAt", {
                  date: formatDate(question.updated),
                })
              }}
            </template>
          </p>
        </div>
      </div>
    </JoszakiCard>
  </div>
</template>

<script>
import { mapState } from "pinia";
import QuestionPills from "./QuestionPills.vue";
import UserAvatar from "~/components/UserAvatar.vue";
import { useUserStore } from "~/stores/user";
import { differenceToNow } from "~/helpers/date";
import {
  MODAL_REPORT_QUESTION,
  useQuestionStore,
} from "~/stores/question/question";

export default {
  name: "CardQuestionDetails",
  components: { UserAvatar, QuestionPills },
  props: {
    question: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      questionStore: useQuestionStore(),
      userStore: useUserStore(),
    };
  },
  data: () => {
    return {
      isSubscribing: false,
    };
  },
  computed: {
    ...mapState(useUserStore, {
      me: "me",
      subscribedQuestionIds: "subscribedQuestionIds",
    }),
    isSubscribed() {
      return this.subscribedQuestionIds.includes(this.question.id);
    },
    subscribeIcon() {
      if (this.isSubscribing) {
        return "cog";
      }

      return this.isSubscribed ? "bell-slash" : "bell";
    },
  },
  methods: {
    formatDate(date) {
      return differenceToNow(this, date);
    },
    async setSubscribe(isSubscribe = true) {
      if (this.isSubscribing) {
        return;
      }

      try {
        this.isSubscribing = true;

        if (isSubscribe) {
          await this.questionStore.subscribeToQuestion(
            Number(this.question.id)
          );
          this.userStore.addQuestionSubscription(this.question.id);
        } else {
          await this.questionStore.unsubscribeFromQuestion(
            Number(this.question.id)
          );
          this.userStore.removeQuestionSubscription(this.question.id);
        }

        this.$joszaki.toast({
          message: this.$t(
            `questionPage.${isSubscribe ? "" : "un"}subscribeSuccess`
          ),
          position: "is-top",
          type: "success",
        });
      } catch (e) {
        this.$joszaki.toast({
          message: this.$t(
            `questionPage.${isSubscribe ? "" : "un"}subscribeError`
          ),
          position: "is-top",
          type: "danger",
        });
      } finally {
        this.isSubscribing = false;
      }
    },
    openModal(modalId) {
      this.questionStore.openModal(modalId, {
        question: { ...this.question },
      });
    },
    openReportModal() {
      if (!this.me) {
        this.$joszaki.toast({
          message: this.$t("questionPage.onlyLoggedInCanReport"),
          position: "is-top",
          type: "info",
        });
        return;
      }

      this.openModal(MODAL_REPORT_QUESTION);
    },
  },
};
</script>
